import { type SerializedSpainClosurePeriod } from '@spendesk/bookkeeping-core-types';
import { addDays } from 'date-fns';

import { useSpainClosurePeriods } from 'src/core/modules/bookkeep/apis/useSpainClosurePeriods';
import { isSpanishDPRPayable } from 'src/core/modules/bookkeep/payables/models/payable';

import type {
  SpainClosedPeriodInfo,
  SpanishDprClosedPeriodCheck,
  Compliances,
} from '../models';

const getFirstAvailableDate = (
  closedPeriods: { to: SerializedSpainClosurePeriod['to'] }[],
): string | undefined => {
  if (closedPeriods.length === 0) {
    return undefined;
  }

  let latestEndDate = new Date(closedPeriods[0].to);
  for (const period of closedPeriods) {
    const periodToDate = new Date(period.to);
    if (periodToDate > latestEndDate) {
      latestEndDate = periodToDate;
    }
  }

  const firstAvailable = addDays(latestEndDate, 1);
  return firstAvailable.toISOString().split('T', 1)[0];
};

const isDateInClosedPeriod = (
  accountingDate: string | Date | null | undefined,
  closedPeriods: SpainClosedPeriodInfo[],
): { isInClosedPeriod: boolean; period?: SpainClosedPeriodInfo } => {
  if (!accountingDate) {
    return { isInClosedPeriod: false };
  }

  const accountingDateValue =
    accountingDate instanceof Date ? accountingDate : new Date(accountingDate);

  for (const period of closedPeriods) {
    if (
      accountingDateValue >= new Date(period.from) &&
      accountingDateValue <= new Date(period.to)
    ) {
      return {
        isInClosedPeriod: true,
        period,
      };
    }
  }

  return { isInClosedPeriod: false };
};

type SpanishDprCheckPayable = {
  documentaryEvidence?: {
    compliances?: Compliances;
  };
  accountingDate: string | Date | null | undefined;
};

export const useSpanishDprClosedPeriodCheck = (
  payable: SpanishDprCheckPayable,
): SpanishDprClosedPeriodCheck => {
  const isSpanishDpr = isSpanishDPRPayable(payable);

  const { isLoading, periods } = useSpainClosurePeriods(
    { state: 'completed' },
    { isEnabled: isSpanishDpr },
  );

  const closedPeriods = Array.isArray(periods) ? periods : [];

  if (!isSpanishDpr) {
    return {
      isInClosedPeriod: false,
      isLoading: false,
    };
  }

  if (isLoading) {
    return {
      isInClosedPeriod: false,
      isLoading: true,
    };
  }

  const { isInClosedPeriod, period } = isDateInClosedPeriod(
    payable.accountingDate,
    closedPeriods,
  );

  const firstAvailableDate = getFirstAvailableDate(closedPeriods);

  return {
    isInClosedPeriod,
    closedPeriod: period,
    firstAvailableDate,
    isLoading: false,
  };
};
